import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routes, webGateRoutes } from "./App";
import { isNative, getPlatform } from "./native";
import { initSafeAreaInsets } from "./native/insets";
import { initSplashControl } from "./native/splashControl";
import { initLiveUpdate } from "./native/liveUpdate";
import { configureAssetDelivery, syncAssets, ASSET_STREAMS } from "./native/assetDelivery";
import { useScanLangStore } from "./store/scanLangStore";
import { i18nReady } from "./i18n"; // i18n 초기화 (Tier 0 골격 — src/i18n/index.ts 규약 참조)
import AppTheme from "./theme/AppTheme";
import { hydrateStoredTheme } from "./store/themeStore";
import { initAnalytics } from "./lib/analytics";
import { env } from "./config/env";
import ErrorBoundary from "./components/layout/ErrorBoundary";
import "./styles/global.css";

// 플랫폼별 CSS 분기용 — <html data-platform="ios|android|web">.
document.documentElement.dataset.platform = getPlatform();

// edge-to-edge 안전영역 — Android 는 env(safe-area-inset-*) 가 항상 0 이라, 네이티브가
// 관측한 시스템 바 인셋으로 --safe-area-* CSS 변수를 채운다 (iOS/web 은 no-op).
void initSafeAreaInsets();

// Splash 안전 hide — capacitor.config 의 2s 와 별개로 React mount 후 즉시 + 5s 안전 timeout
initSplashControl();

// OTA Live Update — notifyAppReady + BE version-policy 체크 (web 에서는 no-op).
initLiveUpdate();

// GA4 (Firebase Analytics) — native 는 즉시 ready, web 은 env 있을 때만 lazy 초기화.
// 초기화 전 track/screen_view 호출은 내부 큐에 쌓였다가 flush 된다.
initAnalytics();

// 저장된 테마 복원 (dev 전용 톤앤매너 전환 — 배포 빌드는 y2k 고정)
hydrateStoredTheme();

// Asset Delivery — 온디바이스 모델·인덱스 배달 (features/asset-delivery PORTING.fe.md §2).
// web 에서는 no-op(sync 즉시 done). 진행률 UI 는 useAssetSync() 로 구독.
// 스트림 = 호환 경계. liner(검출)와 matcher(식별)는 서로 독립이라 따로 발행·수신된다.
// variants 는 인식 언어 — 서버가 인덱스를 그 언어 하나만 내려준다(ko 7.4MB vs 전량 30MB).
void configureAssetDelivery({
  channel: (import.meta.env.VITE_ASSET_CHANNEL ?? "PRODUCTION") as "INTERNAL" | "BETA" | "PRODUCTION",
  appVersion: __APP_VERSION__,
  formats: ["ONNX_1"],
  // ASSET_STREAMS 는 `as const` 라 readonly 튜플이다 — 플러그인은 가변 string[] 를 받으므로
  // 복사해 넘긴다 (ScanPage 의 startScan 호출과 같은 방식).
  streams: [...ASSET_STREAMS],
  variants: [useScanLangStore.getState().lang],
}).then(() => void syncAssets());

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found in index.html");

/**
 * 안내(랜딩) 호스트 판별 — VITE_LANDING_HOST 와 같은 호스트면 앱이 아니라 안내 화면.
 *
 * 배정(2026-09-15 교체):
 *   prod — 앱 = peeka.app(존 apex) · 안내 = app.peeka.app
 *   dev  — 앱 = dev.peeka.app       · 안내 = app.dev.peeka.app
 *
 * 같은 dist 가 두 호스트에 올라간다(S3 frontend/landing · frontend/user, CloudFront 별도).
 * 앱 호스트는 Capacitor WebView 의 hostname 이라 출시 후 못 바꾸는 저장소 origin 이고,
 * 안내 페이지는 가장 자주 개편될 자산이라 호스트를 나눈다. 나중에 정적 사이트나 외부 도구로
 * 갈아탈 때도 안내 호스트에 올리는 것만 바꾸면 되고 앱은 건드리지 않는다.
 */
const isLandingHost =
  !isNative() &&
  !!import.meta.env.VITE_LANDING_HOST &&
  window.location.hostname === import.meta.env.VITE_LANDING_HOST;

/**
 * 웹 비공개 게이트 — gift 외 경로를 ComingSoon 으로 차단하고 공개 약관만 남긴다.
 *
 * ⚠ isNative() AND 조건이 핵심: 네이티브 앱/OTA 는 같은 dist 를 공유하지만 항상 isNative()===true →
 *   게이트가 절대 발동하지 않아 전체 라우트가 정상 동작. (플래그 단독 게이팅 금지 — 앱까지 막힘)
 * - 로컬 dev 서버(import.meta.env.DEV)는 개발 편의를 위해 게이트 우회.
 * - 안내 호스트는 VITE_WEB_PUBLIC 과 **무관하게 항상** 이 셋을 쓴다 — 거기는 앱이 아니다.
 * - 앱 호스트는 .env.dev·.env.prod 모두 VITE_WEB_PUBLIC=true (2026-09-15 웹 공개).
 */
const webGated =
  !isNative() &&
  !import.meta.env.DEV &&
  (isLandingHost || import.meta.env.VITE_WEB_PUBLIC !== "true");

// data router 사용 — useMatches/handle 등 v7 hook 들이 동작하려면 필요.
const router = createBrowserRouter(webGated ? webGateRoutes : routes);

// Web 환경에서 Google GIS 사용. Native 환경에서는 capacitor-google-auth plugin 이 처리.
// Provider 는 web/native 양쪽에서 안전하게 wrap (clientId 빈 문자열도 허용).
const googleClientId = env.VITE_GOOGLE_CLIENT_ID;

/**
 * react-query 단일 데이터 레이어.
 *
 * staleTime 30s — sub-activity 닫고 부모 페이지로 돌아왔을 때 (또는 StrictMode
 * 더블 마운트, popstate sync 후 remount) 동일 key 면 캐시 즉시 반환, 재요청 X.
 *
 * refetchOnWindowFocus false — 모바일 웹뷰 / Capacitor 환경에서 포커스 이벤트로
 * 의도치 않은 refetch 가 발생하는 것을 막는다. 필요한 페이지에서 명시적으로 켤 수 있다.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// i18n 초기화 완료 후 렌더 — 첫 프레임에 키가 노출되는 깜빡임 방지.
// 리소스가 번들 내장이라 사실상 즉시 resolve 되며, 실패해도 렌더는 진행한다(키 노출로 관찰).
void i18nReady.finally(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        {/* Astryx 테마 루트 — html 의 data-astryx-theme/data-theme 를 관리한다 */}
        <AppTheme>
          <QueryClientProvider client={queryClient}>
            <GoogleOAuthProvider clientId={googleClientId}>
              <RouterProvider router={router} />
            </GoogleOAuthProvider>
          </QueryClientProvider>
        </AppTheme>
      </ErrorBoundary>
    </StrictMode>,
  );
});
